import { RouteObject } from 'react-router-dom';
import {
  createEntityAction,
  WorkspaceLayout,
} from '@/components/layouts/WorkspaceLayout/WorkspaceLayout';
import { WorkSpacesList, workspacesLoader } from '@/pages/WorkSpacesList/WorkSpacesList';
import { WorkspaceListErrorElement } from '@/ErrorElements/WorkspaceListErrorElement/WorkspaceListErrorElement';
import { VerifyEmail, verifyEmailLoader } from '@/pages/VerifyEmail/VerifyEmail';
import { VerifyEmailAction } from '@/pages/VerifyEmail/VerifyEmailAction';
import { HandleInvitation } from '@/pages/HandleInvitation/HandleInvitation';
import { handleInvitationLoader } from '@/pages/HandleInvitation/handleInvitationLoader';
import {
  OrganizationSettings,
  organizationSettingsActions,
  organizationSettingsLoader,
} from '@/pages/OrganizationSettings/OrganizationSettings';
import {
  WorkspaceSettings,
  workspaceSettingsActions,
  workspaceSettingsLoader,
} from '@/pages/WorkspaceSettings/WorkspaceSettings';
import {
  Streamlit,
  streamlitLoader,
  streamlitRootLoader,
} from '@/pages/Streamlit/Streamlit';
import { ConnectorsLayout } from '@/components/layouts/ConnectorsLayout/ConnectorsLayout';
import { Connectors, connectorsLoader } from '@/pages/Connectors/Connectors';
import {
  ConnectorsList,
  ConnectorsListLoader,
} from '@/pages/ConnectorsList/ConnectorsList';
import { ConnectorInfo } from '@/pages/ConnectorInfo/ConnectorInfo';
import { EditConnector, EditConnectorLoader } from '@/pages/EditConnector/EditConnector';
import { ConnectorError } from '@/pages/ConnectorError/ConnectorError';
import { FirebaseAuth } from '@/pages/FirebaseAuth/FirebaseAuth';
import { Subscriptions, subscriptionsLoader } from '@/pages/Subscriptions/Subscriptions';
import { StreamlitRoot } from '@/pages/StreamlitRoot/StreamlitRoot';
import { RedirectWithQueryParams } from './RedirectWithQueryParams';
import { MissingPermissions } from '@/pages/MissingPermissions/MissingPermissions';
import { WrongSubscriptionTier } from '@/pages/WrongSubscriptionTier/WrongSubscriptionTier';
import { parallel } from './guards/parallel';
import {
  hasAccess,
  isEmailVerifiedGuard,
  isUserAbleToCreateOrganizations,
  isUserLoggedInGuard,
  isWorkspaceMatchingOrganization,
} from './guards/guards';
import {
  setCanUserCreateOrganizations,
  setOrganizationPermission,
  setWorkspaceId,
  setWorkspacePermission,
} from './guards/setters';

export const rootRoutes: RouteObject[] = [
  {
    path: '',
    element: <WorkspaceLayout />,
    loader: parallel([isUserLoggedInGuard, isEmailVerifiedGuard], {
      loader: workspacesLoader,
      setters: [setWorkspaceId, setCanUserCreateOrganizations],
    }),
    errorElement: <WorkspaceListErrorElement />,
    action: createEntityAction,
    id: 'workspaces',
    children: [
      { path: 'missing-permissions', element: <MissingPermissions /> },
      { path: 'wrong-subscription-tier', element: <WrongSubscriptionTier /> },
      {
        index: true,
        loader: parallel([
          isWorkspaceMatchingOrganization,
          isUserLoggedInGuard,
          isEmailVerifiedGuard,
        ]),
        element: <WorkSpacesList />,
        errorElement: <WorkspaceListErrorElement />,
      },
      {
        path: 'organization-settings',
        element: <OrganizationSettings />,
        loader: parallel(
          [
            isWorkspaceMatchingOrganization,
            isUserLoggedInGuard,
            hasAccess('organization', 'organization', 'view'),
            isEmailVerifiedGuard,
          ],
          {
            loader: organizationSettingsLoader,
            setters: [
              setOrganizationPermission,
              setWorkspacePermission,
              setWorkspaceId,
              setCanUserCreateOrganizations,
            ],
          },
        ),
        errorElement: <WorkspaceListErrorElement />,
        action: organizationSettingsActions,
      },
      {
        path: 'workspace-settings',
        element: <WorkspaceSettings />,
        loader: parallel(
          [
            isWorkspaceMatchingOrganization,
            isUserLoggedInGuard,
            hasAccess('workspace', 'workspace', 'view'),
            isEmailVerifiedGuard,
          ],
          {
            loader: workspaceSettingsLoader,
            setters: [setOrganizationPermission, setWorkspacePermission, setWorkspaceId],
          },
        ),
        action: workspaceSettingsActions,
        errorElement: <WorkspaceListErrorElement />,
      },
      {
        path: 'dashboards',
        element: <StreamlitRoot />,
        loader: parallel(
          [
            isWorkspaceMatchingOrganization,
            isUserLoggedInGuard,
            hasAccess('workspace', 'dashboard', 'view'),
            isEmailVerifiedGuard,
          ],
          {
            loader: streamlitRootLoader,
            setters: [setOrganizationPermission, setWorkspacePermission, setWorkspaceId],
          },
        ),
        children: [
          {
            index: true,
            element: <RedirectWithQueryParams to="general" />,
          },
          {
            path: ':dashboardId',
            element: <Streamlit />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                isUserLoggedInGuard,
                hasAccess('workspace', 'dashboard', 'view'),
                isEmailVerifiedGuard,
              ],
              {
                loader: streamlitLoader,
                setters: [setOrganizationPermission, setWorkspacePermission],
              },
            ),
          },
        ],
        errorElement: <WorkspaceListErrorElement />,
      },
      {
        path: 'subscriptions',
        element: <Subscriptions />,
        loader: parallel(
          [
            isWorkspaceMatchingOrganization,
            isEmailVerifiedGuard,
            isUserAbleToCreateOrganizations,
          ],
          {
            loader: subscriptionsLoader,
            setters: [setCanUserCreateOrganizations],
          },
        ),
      },
      {
        path: 'connectors',
        element: <ConnectorsLayout />,
        errorElement: <WorkspaceListErrorElement />,
        id: 'connectors',
        loader: parallel(
          [
            isWorkspaceMatchingOrganization,
            isUserLoggedInGuard,
            hasAccess('workspace', 'connector', 'view'),
            isEmailVerifiedGuard,
          ],
          {
            loader: connectorsLoader,
            setters: [setOrganizationPermission, setWorkspacePermission, setWorkspaceId],
          },
        ),
        children: [
          {
            index: true,
            element: <Connectors />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                isUserLoggedInGuard,
                hasAccess('workspace', 'connector', 'view'),
              ],
              {
                provisionalSetters: [setOrganizationPermission, setWorkspacePermission],
              },
            ),
          },
          {
            path: 'list',
            element: <ConnectorsList />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                isUserLoggedInGuard,
                hasAccess('workspace', 'connectorsList', 'view'),
              ],
              {
                loader: ConnectorsListLoader,
                provisionalSetters: [setWorkspacePermission],
              },
            ),
          },
          {
            path: 'info/:connectorName',
            element: <ConnectorInfo />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                isUserLoggedInGuard,
                hasAccess('workspace', 'connector', 'update'),
              ],
              {
                provisionalSetters: [setWorkspacePermission],
              },
            ),
          },
          {
            path: 'edit/:connectorName',
            element: <EditConnector />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                hasAccess('workspace', 'connector', 'update'),
              ],
              {
                loader: EditConnectorLoader,
                provisionalSetters: [setWorkspacePermission],
              },
            ),
          },
          {
            path: 'error/:connectorName',
            element: <ConnectorError />,
            loader: parallel(
              [
                isWorkspaceMatchingOrganization,
                isUserLoggedInGuard,
                hasAccess('workspace', 'connector', 'update'),
              ],
              {
                provisionalSetters: [setWorkspacePermission],
              },
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'login',
    element: <FirebaseAuth />,
    loader: parallel([isUserLoggedInGuard]),
  },
  {
    path: 'verify-email',
    element: <VerifyEmail />,
    loader: parallel([isUserLoggedInGuard], {
      loader: verifyEmailLoader,
    }),
    errorElement: <WorkspaceListErrorElement />,
  },
  {
    path: 'verify-email/action',
    element: <VerifyEmailAction />,
  },
  {
    path: 'handleInvitation',
    element: <HandleInvitation />,
    loader: parallel([isUserLoggedInGuard, isEmailVerifiedGuard], {
      loader: handleInvitationLoader,
    }),
    errorElement: <WorkspaceListErrorElement />,
  },
];
